import { useEffect, useState } from "react";
import { Tv, WifiOff, Radio, Loader2 } from "lucide-react";
import { studioApi } from "@/lib/studioApi";

const ago = (iso) => {
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  return s < 5 ? "just now" : s < 60 ? `${s} s ago` : `${Math.round(s / 60)} min ago`;
};

function describe(m) {
  if (m.mode === "broadcast") return { tone: "text-sky-300", Icon: Radio, text: "showing the public broadcast (room link too weak)" };
  if (!m.joined) return { tone: "text-amber-300", Icon: Loader2, text: m.attempt ? `reconnecting (try ${m.attempt})` : "connecting…" };
  if (m.recv && m.recv !== "connected") return { tone: "text-amber-300", Icon: WifiOff, text: `media link ${m.recv}` };
  if (m.tiles > 0 && (m.stalled_ms || 0) > 4000) return { tone: "text-red-300", Icon: WifiOff, text: `no picture for ${Math.round(m.stalled_ms / 1000)} s` };
  return { tone: "text-emerald-300", Icon: Tv, text: m.tiles === 0 ? "in the room · holding card" : "in the room · picture OK" };
}

/** Live health of every screen on the monitor link (they report every 20 s). */
export function MonitorHealth({ showId, enabled = true }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    const load = () => studioApi.listMonitors(showId).then((d) => { if (alive) setData(d); }).catch(() => {});
    load();
    const id = setInterval(load, 10000);
    return () => { alive = false; clearInterval(id); };
  }, [showId, enabled]);
  if (!data) return null;
  const rows = data.monitors || [];
  return (
    <div className="mt-3 border-t border-white/10 pt-3" data-testid="monitor-health">
      <div className="flex items-center justify-between text-[11px] text-zinc-500">
        <span>Screens on this link</span>
        <span data-testid="monitor-health-count">{rows.length === 0 ? "none open" : `${rows.length} open`}</span>
      </div>
      {rows.length > 0 && (
        <ul className="mt-1.5 space-y-1">
          {rows.map((m) => {
            const d = describe(m);
            return (
              <li key={m.monitor_id} className="flex items-center gap-2 rounded-md bg-white/[0.03] px-2 py-1.5 text-xs" data-testid={`monitor-row-${m.monitor_id}`} data-mode={m.mode}>
                <d.Icon className={`h-3.5 w-3.5 shrink-0 ${d.tone} ${d.Icon === Loader2 ? "animate-spin" : ""}`} />
                <span className="font-medium text-zinc-200">{m.label || m.monitor_id}</span>
                <span className={`min-w-0 flex-1 truncate ${d.tone}`}>{d.text}</span>
                <span className="text-[10px] text-zinc-500">{ago(m.updated_at)}</span>
              </li>
            );
          })}
        </ul>
      )}
      {(data.events || []).length > 0 && (
        <details className="mt-2 text-[11px] text-zinc-500">
          <summary className="cursor-pointer">Recent screen events ({data.events.length})</summary>
          <ul className="mt-1 max-h-32 space-y-0.5 overflow-y-auto" data-testid="monitor-events">
            {data.events.map((e, i) => (
              <li key={`${e.at}-${i}`} className="truncate"><span className="text-zinc-400">{new Date(e.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span> · {e.label || e.monitor_id} · <span className="text-zinc-300">{e.event}</span>{e.detail ? ` — ${e.detail}` : ""}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

export default MonitorHealth;
